// src/dnd/views/Sidebar_parts/DiceTab.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import DiceBox from "@3d-dice/dice-box";

const DICE_SELECTION_KEY = "point_atlas_dice_selection_v1";
const DICE_HISTORY_KEY   = "point_atlas_dice_history_v1";
const DICE_THEME_KEY     = "point_atlas_dice_theme_v1";

const DICE_TYPES = [
  { key: "d4",   label: "d4",   faces: 4,   supported: true },
  { key: "d6",   label: "d6",   faces: 6,   supported: true },
  { key: "d8",   label: "d8",   faces: 8,   supported: true },
  { key: "d10",  label: "d10",  faces: 10,  supported: true },
  { key: "d12",  label: "d12",  faces: 12,  supported: true },
  { key: "d20",  label: "d20",  faces: 20,  supported: true },
  { key: "d100", label: "d100", faces: 100, supported: true },
];

function loadSel() {
  try {
    const raw = localStorage.getItem(DICE_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch { return null; }
}
function saveSel(sel) { try { localStorage.setItem(DICE_SELECTION_KEY, JSON.stringify(sel)); } catch {} }
function loadHist() {
  try {
    const raw = localStorage.getItem(DICE_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function saveHist(hist) { try { localStorage.setItem(DICE_HISTORY_KEY, JSON.stringify(hist)); } catch {} }
function loadTheme() {
  try {
    const raw = localStorage.getItem(DICE_THEME_KEY);
    if (!raw) return null;
    const val = JSON.parse(raw);
    return typeof val === "string" && val ? val : null;
  } catch { return null; }
}
function saveTheme(theme) { try { localStorage.setItem(DICE_THEME_KEY, JSON.stringify(theme)); } catch {} }

// IMPORTANT: run DiceBox on main thread so we can intercept Babylon’s banner.
function configForSize(widthPx, heightPx) {
  const simEdge = Math.max(1, Math.min(widthPx || 0, heightPx || 0));
  const base  = simEdge / 48;
  const scale = Math.max(8, Math.min(12, base * 1.6));
  return {
    scale,
    gravity: 1.28,
    friction: 0.95,
    restitution: 0,
    angularDamping: 0.58,
    linearDamping: 0.62,
    spinForce: 2.8,
    throwForce: 3.4,
    startingHeight: 6,
    delay: 12,
    lightIntensity: 1.25,
    enableShadows: true,
    shadowTransparency: 0.7,
    theme: "default",
    themeColor: "#d6d6d6",
    // 🔑 KEY CHANGE: no OffscreenCanvas/Web Worker → banner stays on main thread and can be silenced
    offscreen: false,
  };
}

// Silences only the Babylon version banner during a given async section.
async function withBabylonBannerSilenced(asyncFn) {
  const origInfo = console.info;
  const origLog  = console.log;
  try {
    console.info = function (...args) {
      if (args.length && typeof args[0] === "string" && /^Babylon\.js v/i.test(args[0])) return;
      return origInfo.apply(this, args);
    };
    console.log = function (...args) {
      if (args.length && typeof args[0] === "string" && /^Babylon\.js v/i.test(args[0])) return;
      return origLog.apply(this, args);
    };
    return await asyncFn();
  } finally {
    console.info = origInfo;
    console.log  = origLog;
  }
}

export default function DiceTab({
  styles = {},
  TIP_BAR_HEIGHT = 68,
  onDiceRoll,
  trayMaxPx = 260,
  trayAspect = 1.0,
}) {
  const defaultSel = useMemo(() => {
    const saved = loadSel();
    if (saved) return saved;
    const init = { d4: 0, d6: 2, d8: 0, d10: 0, d12: 0, d20: 1, d30: 0, d100: 0 };
    saveSel(init);
    return init;
  }, []);

  const [selection, setSelection] = useState(defaultSel);
  const [history, setHistory]     = useState(loadHist());
  const [isRolling, setIsRolling] = useState(false);
  const [mode, setMode]           = useState("normal");
  const [theme, setTheme]         = useState(() => loadTheme() || "default");

  useEffect(() => saveSel(selection), [selection]);
  useEffect(() => saveHist(history), [history]);
  useEffect(() => saveTheme(theme), [theme]);

  const totalSelected = Object.values(selection).reduce((a, b) => a + (b | 0), 0);

  const bump = (key, delta) => {
    setSelection((prev) => {
      const next = { ...prev, [key]: Math.max(0, Math.min(99, (prev[key] | 0) + delta)) };
      return next;
    });
  };
  const reset = () => setSelection({ d4: 0, d6: 2, d8: 0, d10: 0, d12: 0, d20: 1, d30: 0, d100: 0 });
  const clear = () => setHistory([]);

  const trayRef       = useRef(null);
  const CANVAS_ID     = "pa-dicebox-fullsize";
  const diceBoxRef    = useRef(null);
  const themeReadyRef = useRef(false);
  const modeRef       = useRef("normal");
  useEffect(() => { modeRef.current = mode; }, [mode]);

  useEffect(() => {
    const el = trayRef.current;
    if (!el) return;

    let raf = 0;
    const updateSizeAndConfig = () => {
      const w = el.clientWidth || 220;
      const h = Math.round(w * trayAspect);
      el.style.height = `${h}px`;
      if (diceBoxRef.current) {
        if (typeof diceBoxRef.current.resize === "function") {
          diceBoxRef.current.resize();
        }
        if (themeReadyRef.current && typeof diceBoxRef.current.updateConfig === "function") {
          diceBoxRef.current.updateConfig({ ...configForSize(w, h), theme });
        }
      }
    };

    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateSizeAndConfig);
    });
    ro.observe(el);
    updateSizeAndConfig();

    return () => {
      try { ro.disconnect(); } catch {}
      cancelAnimationFrame(raf);
    };
  }, [trayAspect, theme]);

  useEffect(() => {
    const el = trayRef.current;
    if (!el) return;

    const w = el.clientWidth  || 220;
    const h = el.clientHeight || Math.round(w * trayAspect);

    const box = new DiceBox({
      assetPath: "/assets/dice-box/",
      container: `#${CANVAS_ID}`,
      ...configForSize(w, h),
      theme,
      onThemeLoaded: () => { themeReadyRef.current = true; },
      onRollComplete: (groups) => {
        const currentMode = modeRef.current;
        const dice = [];
        const totals = { perType: {}, overall: 0 };
        const bySides = new Map();
        for (const g of groups || []) {
          const arr = bySides.get(g.sides) || [];
          arr.push(...(g.rolls || []).map(r => r.value));
          bySides.set(g.sides, arr);
        }
        const d20Values = bySides.get(20) || [];
        if (currentMode === "adv" || currentMode === "dis") {
          for (let i = 0; i < d20Values.length; i += 2) {
            const a = d20Values[i];
            const b = d20Values[i + 1];
            if (typeof a !== "number" || typeof b !== "number") break;
            const kept = currentMode === "adv" ? Math.max(a, b) : Math.min(a, b);
            dice.push({ type: "d20", value: kept, detail: [a, b], mode: currentMode });
            totals.perType.d20 = (totals.perType.d20 || 0) + kept;
            totals.overall += kept;
          }
          bySides.delete(20);
        }
        for (const [sides, values] of bySides.entries()) {
          const type = `d${sides}`;
          for (const v of values) {
            dice.push({ type, value: v });
            totals.perType[type] = (totals.perType[type] || 0) + v;
            totals.overall += v;
          }
        }
        const entry = { timestamp: Date.now(), dice, totals, mode: currentMode };
        setHistory((h) => {
          const next = [...h, entry];
          if (next.length > 500) next.shift();
          return next;
        });
      },
    });

    diceBoxRef.current = box;

    // 🔕 Silence the Babylon banner strictly during init
    (async () => {
      try {
        await withBabylonBannerSilenced(async () => {
          await box.init();
        });
        if (box.resize) box.resize();
      } catch (e) {
        console.error("[DiceBox] init failed:", e);
      }
    })();

    return () => { try { box.clear(); } catch {} };
  }, [trayAspect, theme]);

  function buildNotations() {
    const modeNow = modeRef.current;
    const out = [];
    for (const d of DICE_TYPES) {
      let count = selection[d.key] | 0;
      if (!count || !d.supported) continue;
      if (d.faces === 20 && (modeNow === "adv" || modeNow === "dis")) count = count * 2;
      out.push({ qty: count, sides: d.faces });
    }
    return out;
  }

  const roll = async () => {
    const notations = buildNotations();
    if (notations.length === 0 || !diceBoxRef.current) return;

    if (typeof onDiceRoll === "function") {
      try {
        onDiceRoll({
          items: DICE_TYPES.map((d) => ({ type: d.key, count: selection[d.key] | 0 })).filter((x) => x.count > 0),
          mode: modeRef.current,
          timestamp: Date.now(),
        });
      } catch {}
    }

    setIsRolling(true);
    try {
      await withBabylonBannerSilenced(async () => {
        await diceBoxRef.current.roll(notations, { theme });
      });
    } catch (e) {
      console.error("[DiceBox] roll error:", e);
    } finally {
      setIsRolling(false);
    }
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <div
        style={{
          padding: "8px 10px",
          borderBottom: "1px solid #333",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <strong>Dice</strong>
        <span style={{ opacity: 0.7 }}>• {totalSelected} </span>

        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            onClick={roll}
            disabled={totalSelected === 0 || isRolling}
            style={{
              background: totalSelected === 0 || isRolling ? "#333" : "#ffc73c",
              color: totalSelected === 0 || isRolling ? "#888" : "#1f1800",
              border: "none",
              borderRadius: 8,
              padding: "6px 10px",
              fontWeight: 800,
              cursor: totalSelected === 0 || isRolling ? "not-allowed" : "pointer",
            }}
            title={mode === "adv" ? "Roll (Advantage)" : mode === "dis" ? "Roll (Disadvantage)" : "Roll"}
          >
            {isRolling ? "Rolling…" : "Roll"}
          </button>
          <button
            onClick={reset}
            disabled={isRolling}
            style={{
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8,
              padding: "6px 10px",
              fontWeight: 700,
              cursor: isRolling ? "not-allowed" : "pointer",
            }}
          >
            Reset
          </button>
          <button
            onClick={clear}
            disabled={isRolling}
            style={{
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8,
              padding: "6px 10px",
              fontWeight: 700,
              cursor: isRolling ? "not-allowed" : "pointer",
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div
        style={{
          padding: "6px 10px 4px 10px",
          borderBottom: "1px solid #2a2b2f",
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 12, opacity: 0.75 }}>Mode:</span>
        <button
          onClick={() => setMode("normal")}
          disabled={isRolling}
          style={{
            padding: "4px 8px",
            borderRadius: 8,
            border: "1px solid " + (mode === "normal" ? "#ffc73c" : "rgba(255,255,255,0.2)"),
            background: mode === "normal" ? "rgba(255,199,60,0.15)" : "transparent",
            color: "#fff",
            fontWeight: 700,
            cursor: isRolling ? "not-allowed" : "pointer",
            fontSize: 12,
          }}
          title="Normal rolls"
        >
          Normal
        </button>
        <button
          onClick={() => setMode("adv")}
          disabled={isRolling}
          style={{
            padding: "4px 8px",
            borderRadius: 8,
            border: "1px solid " + (mode === "adv" ? "#ffc73c" : "rgba(255,255,255,0.2)"),
            background: mode === "adv" ? "rgba(255,199,60,0.15)" : "transparent",
            color: "#fff",
            fontWeight: 700,
            cursor: isRolling ? "not-allowed" : "pointer",
            fontSize: 12,
          }}
          title="Advantage (d20 only)"
        >
          Adv
        </button>
        <button
          onClick={() => setMode("dis")}
          disabled={isRolling}
          style={{
            padding: "4px 8px",
            borderRadius: 8,
            border: "1px solid " + (mode === "dis" ? "#ffc73c" : "rgba(255,255,255,0.2)"),
            background: mode === "dis" ? "rgba(255,199,60,0.15)" : "transparent",
            color: "#fff",
            fontWeight: 700,
            cursor: isRolling ? "not-allowed" : "pointer",
            fontSize: 12,
          }}
          title="Disadvantage (d20 only)"
        >
          Dis
        </button>
      </div>

      <div
        style={{
          padding: "6px 10px 6px 10px",
          borderBottom: "1px solid #2a2b2f",
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: 6,
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 12, opacity: 0.75 }}>Theme:</span>
        <select
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          disabled={isRolling}
          style={{
            background: "#2a2b2f",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#fff",
            borderRadius: 8,
            padding: "6px 8px",
          }}
          title="Dice theme"
        >
          <option value="default">Default</option>
          <option value="diceOfRolling">Dice of Rolling</option>
          <option value="gemstone">Gemstone</option>
          <option value="rock">Rock</option>
          <option value="rust">Rust</option>
          <option value="smooth">Smooth</option>
          <option value="wooden">Wooden</option>
          <option value="blueGreenMetal">Blue Green Metal</option>
        </select>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ padding: "8px 10px 10px 10px", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 }}>
          {DICE_TYPES.map((d) => {
            const count = selection[d.key] | 0;
            const disabled = !d.supported;
            return (
              <div
                key={d.key}
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 10,
                  padding: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: disabled ? 0.45 : 1,
                }}
                title={disabled ? "Not available in default dice theme" : undefined}
              >
                <div style={{ fontWeight: 800, width: 36 }}>{d.label}</div>
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    onClick={() => !disabled && bump(d.key, -1)}
                    aria-label={`Decrease ${d.label}`}
                    disabled={isRolling || disabled}
                    style={{
                      width: 24, height: 24, borderRadius: 6,
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.08)",
                      color: "#fff", cursor: isRolling || disabled ? "not-allowed" : "pointer",
                      lineHeight: 1,
                    }}
                  >
                    −
                  </button>
                  <div style={{ minWidth: 22, textAlign: "center", fontWeight: 800, fontSize: 13 }}>{count}</div>
                  <button
                    onClick={() => !disabled && bump(d.key, +1)}
                    aria-label={`Increase ${d.label}`}
                    disabled={isRolling || disabled}
                    style={{
                      width: 24, height: 24, borderRadius: 6,
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.08)",
                      color: "#fff", cursor: isRolling || disabled ? "not-allowed" : "pointer",
                      lineHeight: 1,
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: "0 10px 8px 10px" }}>
          <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <div
              ref={trayRef}
              id={CANVAS_ID}
              style={{
                width: "100%",
                maxWidth: trayMaxPx,
                aspectRatio: `1 / ${trayAspect}`,
                background: "#0f0f10",
                borderTop: "1px solid #333",
                borderRadius: 8,
                overflow: "hidden",
                position: "relative",
              }}
            />
          </div>
          <style>{`
            #${CANVAS_ID} canvas, 
            #${CANVAS_ID} video, 
            #${CANVAS_ID} .overlay {
              width: 100% !important;
              height: 100% !important;
              display: block;
              position: absolute;
              inset: 0;
            }
          `}</style>
        </div>

        <div style={{ overflowY: "auto", padding: "8px 10px 12px 10px", minHeight: 0 }}>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 10 }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>History</div>
            {history.length === 0 ? (
              <div style={{ opacity: 0.7 }}>No rolls yet.</div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {history.slice(-100).reverse().map((h, idx) => (
                  <li
                    key={idx}
                    style={{
                      padding: "6px 0",
                      borderBottom: "1px dashed rgba(255,255,255,0.06)",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      alignItems: "baseline",
                    }}
                  >
                    <span style={{ opacity: 0.75 }}>
                      {new Date(h.timestamp || Date.now()).toLocaleTimeString()}
                      {h.mode && h.mode !== "normal" ? ` [${h.mode === "adv" ? "Adv" : "Dis"}]` : ""}
                    </span>
                    <span style={{ fontWeight: 700 }}>
                      {Array.isArray(h.dice)
                        ? h.dice
                            .map((d) =>
                              d.detail && (d.mode === "adv" || d.mode === "dis")
                                ? `${d.value} (${d.detail[0]},${d.detail[1]})`
                                : `${d.value}`
                            )
                            .join(", ")
                        : ""}
                      {typeof h.totals?.overall === "number" ? ` = ${h.totals.overall}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
